import type {DropdownRepository} from '../hooks/useCopilotRepoFetching'
import {CopilotAssignmentForm} from './CopilotAssignmentForm'
import type {CopilotAssignmentBaseProps} from './copilot-assignment-types'
import {Dialog} from '@primer/react'
import {Banner} from '@primer/react/experimental'
import {useCallback, useMemo, useState} from 'react'
import {assignIssuesToCopilot} from '../utils/copilot-assignment-api'

export interface CopilotAssignmentModalProps extends CopilotAssignmentBaseProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Modal component for Copilot-assisted issue assignment.
 * Uses CopilotAssignmentForm component for repo/branch selection and custom instructions.
 * Can be used independently of the button component.
 */
export function CopilotAssignmentModal({
  isOpen,
  onClose,
  repository,
  issueNumbers,
  onAssignmentComplete,
}: CopilotAssignmentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Derive owner and repo from repository object
  const owner = repository.owner.login
  const repo = repository.name

  const multipleIssues = issueNumbers ? issueNumbers.length > 1 : false

  const handleSubmit = useCallback(
    async (content: string, selectedRepo: DropdownRepository | null, selectedBranch: string) => {
      if (!selectedRepo || !issueNumbers || issueNumbers.length === 0) {
        return
      }

      setIsSubmitting(true)
      setErrorMessage(null)

      try {
        // Make API call to Issues controller for Copilot assignment
        const response = await assignIssuesToCopilot(owner, repo, issueNumbers, {
          instructions: content,
          targetRepo: selectedRepo.nameWithOwner,
          targetBranch: selectedBranch,
        })

        if (response.ok) {
          onClose()
          onAssignmentComplete?.()
          // Success will be shown by assignees changing, no toast needed
        } else {
          setErrorMessage(response.errorMessage || `Failed to assign issue${multipleIssues ? 's' : ''} to Copilot`)
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred')
      } finally {
        setIsSubmitting(false)
      }
    },
    [owner, repo, issueNumbers, onAssignmentComplete, onClose, multipleIssues],
  )

  // Convert repository to the format expected by CopilotAssignmentForm
  const initialRepo = useMemo(
    () =>
      repository
        ? {
            databaseId: repository.databaseId || 0,
            name: repository.name,
            owner: {
              login: repository.owner.login,
            },
            isPrivate: repository.isPrivate || false,
          }
        : null,
    [repository],
  )

  if (!isOpen || !issueNumbers) {
    return null
  }

  const title = multipleIssues ? `Assign ${issueNumbers.length} issues to Copilot` : 'Assign Copilot to issue'

  const description = `Copilot will create a pull request for ${
    multipleIssues ? 'these issues' : 'this issue'
  } in the selected repository and branch with any additional prompt specified here.`

  return (
    <Dialog title={title} onClose={onClose} width="xlarge">
      <p style={{marginBottom: '16px', color: 'var(--fgColor-muted)'}}>{description}</p>
      {errorMessage && (
        <div style={{marginBottom: '16px'}}>
          <Banner
            variant="critical"
            title="Assignment Failed"
            hideTitle
            description={errorMessage}
            secondaryAction={
              <Banner.SecondaryAction onClick={() => setErrorMessage(null)}>Dismiss</Banner.SecondaryAction>
            }
          />
        </div>
      )}
      <CopilotAssignmentForm
        initialRepo={initialRepo}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  )
}

try{ CopilotAssignmentModal.displayName ||= 'CopilotAssignmentModal' } catch {}